import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, distinctUntilChanged, switchMap, takeUntil } from 'rxjs';
import { CarbonTable } from 'src/app/components/carbon-table/carbon-table.component';
import { CarbonCredit } from 'src/app/shared/interfaces/carbon-credit';
import { CarbonCreditConsultation } from 'src/app/shared/interfaces/consultation-credit.model';
import { Pagination } from 'src/app/shared/interfaces/pagination';
import { CsvExportService } from 'src/app/shared/services/csv-export.service';

import { CarbonFilter } from '../interfaces/carbon-filter';
import { CharacteristicConsultService } from '../services/characteristic-consult.service';
import {
  CharacteristicConsultStatusBadge,
  resolveCharacteristicConsultStatusBadge,
} from '../shared/characteristic-consult-status-badge.utils';
import {
  countActiveFilters,
  createCharacteristicConsultPagination,
  flattenCarbonCreditConsultationRow,
} from '../shared/characteristic-consult.utils';
import { CharacteristicConsultListService } from '../shared/characteristic-consult-list.service';

@Component({
  standalone: false,
  selector: 'app-characteristic-consult-list',
  templateUrl: './characteristic-consult-list.component.html',
  styleUrls: ['./characteristic-consult-list.component.scss'],
})
export class CharacteristicConsultListComponent extends CarbonTable<CarbonCredit> implements OnInit, OnDestroy {
  private static readonly PAGE_SIZE = 10;

  @Output() creditCarbonDetail = new EventEmitter<CarbonCredit>();
  @Output() valueFilterTotal = new EventEmitter<number>();
  @Output() valueTotal = new EventEmitter<number>();
  @Output() exportSucceeded = new EventEmitter<void>();
  @Output() exportFailed = new EventEmitter<void>();
  @Output() loadFailed = new EventEmitter<void>();

  private static readonly REQUEST_OPTIONS = { suppressLegacyErrorModal: true };

  private readonly destroy$ = new Subject<void>();
  private lastSuccessfulPage = 1;

  override pagination: Pagination<CarbonCredit> = createCharacteristicConsultPagination(
    CharacteristicConsultListComponent.PAGE_SIZE,
  );

  constructor(
    private readonly carbonService: CharacteristicConsultService,
    private readonly characteristicConsultListService: CharacteristicConsultListService,
    private readonly csvExportService: CsvExportService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.onFilterChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get creditsFoundLabel(): string {
    const count = this.totalItens ?? 0;
    const hasActiveFilters = countActiveFilters(
      this.characteristicConsultListService.filters.getValue(),
    ) > 0;

    if (hasActiveFilters) {
      return count === 1
        ? '1 resultado encontrado para o filtro selecionado'
        : `${count} resultados encontrados para o filtro selecionado`;
    }

    return count === 1
      ? '1 resultado encontrado'
      : `${count} resultados encontrados`;
  }

  get recordsPerPageLabel(): string {
    const pageSize = this.pagination.size ?? CharacteristicConsultListComponent.PAGE_SIZE;
    return pageSize === 1
      ? 'Mostrando 1 registro por página'
      : `Mostrando ${pageSize} registros por página`;
  }

  trackByCarbonCreditCode(_index: number, credit: CarbonCredit): string {
    return credit.carbonCreditCode ?? `${_index}`;
  }

  getStatusBadge(credit: CarbonCredit): CharacteristicConsultStatusBadge {
    return resolveCharacteristicConsultStatusBadge(credit.statusCodeDescription);
  }

  getDetailsLabel(credit: CarbonCredit): string {
    return `Visualizar detalhes do crédito ${credit.carbonCreditCode || ''}`.trim();
  }

  onPaginationChange(event: Event): void {
    const customEvent = event as CustomEvent<{ current: number; previous: number }>;
    const nextPage = customEvent.detail?.current;

    if (nextPage && nextPage !== this.pagination.page) {
      this.onPageChange(nextPage);
    }
  }

  onPageChange(currentPage: number): void {
    if (this.totalPages > 0 && currentPage > this.totalPages) {
      return;
    }

    this.lastSuccessfulPage = this.pagination.page ?? 1;
    this.pagination.page = currentPage;
    this.fetchCurrentPage();
  }

  override resetPagination(): void {
    this.pagination = createCharacteristicConsultPagination(
      CharacteristicConsultListComponent.PAGE_SIZE,
    );
  }

  openDetail(row: CarbonCredit): void {
    this.creditCarbonDetail.emit(row);
  }

  reloadCurrentQuery(): void {
    const filters = this.characteristicConsultListService.filters.getValue();
    this.emitFilterTotal(filters);
    this.getCarbonCredit(filters, this.pagination);
  }

  imprimir(): void {
    const filters = this.characteristicConsultListService.filters.getValue();

    this.carbonService.exportCredits(
      filters,
      CharacteristicConsultListComponent.REQUEST_OPTIONS,
    ).pipe(
      takeUntil(this.destroy$),
    ).subscribe({
      next: (rows) => {
        if (!rows?.length) {
          return;
        }

        this.csvExportService.exportToCSV(
          rows as unknown as CarbonCreditConsultation[],
          'Consulta de Crédito de Carbono.csv',
          flattenCarbonCreditConsultationRow,
        );
        this.exportSucceeded.emit();
      },
      error: () => this.exportFailed.emit(),
    });
  }

  private onFilterChange(): void {
    this.characteristicConsultListService.filters
      .pipe(
        distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
        switchMap((filters) => {
          this.resetPagination();
          this.emitFilterTotal(filters);
          return this.carbonService.queryCredits(
            filters,
            this.pagination,
            CharacteristicConsultListComponent.REQUEST_OPTIONS,
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ results, total, totalPages }) => this.applyQueryResult(results, total, totalPages),
        error: () => {
          this.applyQueryResult([], 0);
          this.loadFailed.emit();
        },
      });
  }

  private applyQueryResult(
    results: CarbonCredit[],
    total: number,
    totalPages?: number,
  ): void {
    this.itens = results;
    this.totalItens = total;
    this.updateTotalPages(totalPages);
    this.lastSuccessfulPage = this.pagination.page ?? 1;
    this.valueTotal.emit(this.totalItens);
  }

  private updateTotalPages(totalPages?: number): void {
    if (totalPages != null && totalPages >= 0) {
      this.totalPages = totalPages;
      return;
    }

    this.handleTotalPages(null);
  }

  private fetchCurrentPage(): void {
    const filters = this.characteristicConsultListService.filters.getValue();
    this.emitFilterTotal(filters);
    this.getCarbonCredit(filters, this.pagination);
  }

  private getCarbonCredit(filter: CarbonFilter, pagination: Pagination<CarbonCredit>): void {
    this.carbonService.queryCredits(
      filter,
      pagination,
      CharacteristicConsultListComponent.REQUEST_OPTIONS,
    ).pipe(
      takeUntil(this.destroy$),
    ).subscribe({
      next: ({ results, total, totalPages }) => this.applyQueryResult(results, total, totalPages),
      error: () => {
        this.pagination.page = this.lastSuccessfulPage;
        this.loadFailed.emit();
      },
    });
  }

  private emitFilterTotal(filters: CarbonFilter): void {
    setTimeout(() => {
      this.valueFilterTotal.emit(countActiveFilters(filters));
    });
  }
}
